/**
 * Text messages (SMS) through each business's own Twilio account — the pure,
 * runtime-agnostic half: phone normalisation, sender validation, message
 * builders, Twilio request shaping, webhook signature checks and the "should we
 * send this?" decision. No `Deno.*`, no remote imports, no I/O, so it runs in
 * the Node/Playwright unit tests, the browser (src/lib/sms.ts) and Deno.
 *
 * Twilio API (verified against twilio.com/docs, Nov 2026):
 *   - Send:   POST https://api.twilio.com/2010-04-01/Accounts/{AccountSid}/Messages.json
 *             form-encoded To, Body, and From OR MessagingServiceSid, optional
 *             StatusCallback. HTTP Basic auth AccountSid:AuthToken.
 *   - Verify: GET  https://api.twilio.com/2010-04-01/Accounts/{AccountSid}.json
 *             (status: active | suspended | closed; type: Trial | Full).
 *   - Webhook signature (X-Twilio-Signature): base64(HMAC-SHA1(authToken,
 *             url + concat(sorted(params) as key+value))). Signed with the
 *             account's Auth Token, which is why we connect with the Auth Token.
 */

export const TWILIO_API_BASE = "https://api.twilio.com/2010-04-01";

export type SmsMessageType = "booking_confirmation" | "appointment_reminder" | "quote_link" | "test";
export type SmsSenderKind = "phone" | "messaging_service" | "alphanumeric";

// ---------------------------------------------------------------------------
// Phone numbers
// ---------------------------------------------------------------------------

export type PhoneResult =
  | { ok: true; e164: string; reason?: undefined }
  | { ok: false; reason: "empty" | "landline" | "not_mobile" | "invalid"; e164?: undefined };

/**
 * Turn a UK mobile number in any everyday format into E.164 (+447…).
 * 07700 900123, +44 7700 900123, 0044 7700 900123, 44 7700 900123 and
 * +44 (0)7700 900123 all become +447700900123. Landlines (01/02/03), 070
 * personal numbers, 076 pagers (except 07624, Isle of Man mobiles) and anything
 * that isn't 11 UK digits are refused: texts go to mobiles only.
 */
export function normaliseUkMobile(raw: string | null | undefined): PhoneResult {
  const input = String(raw ?? "").trim();
  if (!input) return { ok: false, reason: "empty" };
  // Only digits, spaces and common separators are allowed.
  if (!/^[+\d\s().\-/]+$/.test(input)) return { ok: false, reason: "invalid" };

  let s = input.replace(/\(0\)/g, "").replace(/[\s().\-/]/g, "");
  if (s.startsWith("+")) s = s.slice(1);
  else if (s.startsWith("00")) s = s.slice(2);
  else if (s.startsWith("0")) s = `44${s.slice(1)}`;
  if (!/^\d+$/.test(s)) return { ok: false, reason: "invalid" };
  if (!s.startsWith("44")) return { ok: false, reason: "invalid" };

  let national = s.slice(2);
  // "+44 07…" (a stray trunk zero after the country code).
  if (national.startsWith("0")) national = national.slice(1);
  if (!/^\d{10}$/.test(national)) return { ok: false, reason: "invalid" };

  if (/^[123]/.test(national)) return { ok: false, reason: "landline" };
  if (!national.startsWith("7")) return { ok: false, reason: "not_mobile" };
  // 070 = personal numbering, 076 = pagers (07624 is an Isle of Man mobile).
  if (national.startsWith("70")) return { ok: false, reason: "not_mobile" };
  if (national.startsWith("76") && !national.startsWith("7624")) return { ok: false, reason: "not_mobile" };
  return { ok: true, e164: `+44${national}` };
}

export function phoneProblem(result: PhoneResult): string | null {
  if (result.ok) return null;
  switch (result.reason) {
    case "empty": return "Enter a mobile number.";
    case "landline": return "That looks like a landline. Texts can only go to mobiles.";
    case "not_mobile": return "That isn't a UK mobile number.";
    default: return "Enter a UK mobile number, like 07700 900123.";
  }
}

/** "+447700900123" -> "+44 7*** ***123". Safe to store and show. */
export function maskPhone(e164: string): string {
  const digits = e164.replace(/\D/g, "");
  if (digits.length < 6) return "***";
  const last = digits.slice(-3);
  if (digits.startsWith("44")) return `+44 ${digits.charAt(2)}*** ***${last}`;
  return `+${digits.slice(0, 2)} ***${last}`;
}

// ---------------------------------------------------------------------------
// Credentials and sender
// ---------------------------------------------------------------------------

export const ACCOUNT_SID_RE = /^AC[0-9a-fA-F]{32}$/;
export const AUTH_TOKEN_RE = /^[0-9a-fA-F]{32}$/;
export const MESSAGING_SERVICE_SID_RE = /^MG[0-9a-fA-F]{32}$/;

export function accountSidProblem(value: string | null | undefined): string | null {
  const v = String(value ?? "").trim();
  if (!v) return "Enter your Account SID.";
  if (!ACCOUNT_SID_RE.test(v)) return "Your Account SID starts with AC and is 34 characters long.";
  return null;
}

export function authTokenProblem(value: string | null | undefined): string | null {
  const v = String(value ?? "").trim();
  if (!v) return "Enter your Auth Token.";
  if (!AUTH_TOKEN_RE.test(v)) return "Your Auth Token is 32 letters and numbers. Copy it from the Twilio Console.";
  return null;
}

export type SenderResult =
  | { ok: true; kind: SmsSenderKind; value: string; message?: undefined }
  | { ok: false; message: string; kind?: undefined; value?: undefined };

/**
 * Validate the "send from" value.
 *   phone             a Twilio number in E.164 (UK numbers may be typed as 07…)
 *   messaging_service a Messaging Service SID (MG…)
 *   alphanumeric      a sender name: 1–11 letters, numbers and spaces with at
 *                     least one letter. UK networks accept these, but
 *                     customers can't reply to them.
 */
export function parseSmsSender(kind: string, raw: string | null | undefined): SenderResult {
  const value = String(raw ?? "").trim();
  if (kind === "phone") {
    if (!value) return { ok: false, message: "Enter your Twilio phone number." };
    const compact = value.replace(/[\s().-]/g, "");
    if (/^\+(?!44)\d{8,15}$/.test(compact)) return { ok: true, kind, value: compact };
    const uk = normaliseUkMobile(value);
    if (uk.ok) return { ok: true, kind, value: uk.e164 };
    // UK geographic numbers can't send texts; anything else must be E.164.
    const ukAny = compact.replace(/^0(?=\d{10}$)/, "+44").replace(/^0044/, "+44");
    if (/^\+44\d{10}$/.test(ukAny)) return { ok: false, message: "That number can't send texts. Use a Twilio mobile number (+447…)." };
    return { ok: false, message: "Enter the number in full, like +447700900123." };
  }
  if (kind === "messaging_service") {
    if (!MESSAGING_SERVICE_SID_RE.test(value)) return { ok: false, message: "A Messaging Service SID starts with MG and is 34 characters long." };
    return { ok: true, kind, value };
  }
  if (kind === "alphanumeric") {
    if (!/^[A-Za-z0-9 ]{1,11}$/.test(value)) return { ok: false, message: "Use up to 11 letters, numbers or spaces." };
    if (!/[A-Za-z]/.test(value)) return { ok: false, message: "Your sender name needs at least one letter." };
    return { ok: true, kind, value };
  }
  return { ok: false, message: "Choose how your texts are sent." };
}

export function senderLabel(kind: string | null | undefined, value: string | null | undefined): string {
  if (!kind || !value) return "";
  if (kind === "messaging_service") return `Messaging Service …${value.slice(-4)}`;
  if (kind === "alphanumeric") return `Sender name “${value}”`;
  return value;
}

/** Basic auth header for Twilio (AccountSid:AuthToken). */
export function twilioAuthHeader(accountSid: string, authToken: string): string {
  return `Basic ${btoa(`${accountSid}:${authToken}`)}`;
}

/** Form body for Messages.json. */
export function buildTwilioMessageForm(input: {
  to: string;
  body: string;
  senderKind: SmsSenderKind | string;
  senderValue: string;
  statusCallback?: string | null;
}): URLSearchParams {
  const form = new URLSearchParams();
  form.set("To", input.to);
  if (input.senderKind === "messaging_service") form.set("MessagingServiceSid", input.senderValue);
  else form.set("From", input.senderValue);
  form.set("Body", input.body);
  if (input.statusCallback) form.set("StatusCallback", input.statusCallback);
  return form;
}

// ---------------------------------------------------------------------------
// Message builders
// ---------------------------------------------------------------------------

export const SMS_SINGLE_LIMIT = 160;

// GSM 03.38 basic set + extension table. Anything else forces UCS-2 (70 chars).
const GSM_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM_EXT = "^{}\\[~]|€";

export function isGsm7(text: string): boolean {
  for (const ch of text) if (!GSM_BASIC.includes(ch) && !GSM_EXT.includes(ch)) return false;
  return true;
}

/** Characters as a phone network counts them (extension chars count twice). */
export function gsmLength(text: string): number {
  let n = 0;
  for (const ch of text) n += GSM_EXT.includes(ch) ? 2 : 1;
  return n;
}

/** How many texts a message will be billed as. */
export function smsSegments(text: string): number {
  if (isGsm7(text)) {
    const len = gsmLength(text);
    return len <= 160 ? 1 : Math.ceil(len / 153);
  }
  const len = [...text].length;
  return len <= 70 ? 1 : Math.ceil(len / 67);
}

/** Swap typographic characters for plain ones so texts stay in the cheap GSM set. */
export function toGsmFriendly(text: string): string {
  return String(text ?? "")
    .replace(/[‘’‚′]/g, "'")
    .replace(/[“”„″]/g, '"')
    .replace(/[–—−]/g, "-")
    .replace(/…/g, "...")
    .replace(/[\u00A0\u2007\u202F]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function clip(text: string, max: number): string {
  const t = toGsmFriendly(text);
  if (t.length <= max) return t;
  return `${t.slice(0, Math.max(1, max - 3)).trimEnd()}...`;
}

/**
 * Assemble a text from a required core and optional tails, dropping tails and
 * then shortening the flexible parts until it fits one text (160 GSM chars).
 */
function fit(build: (biz: string, item: string) => string, biz: string, item: string, tails: string[]): string {
  const candidates: string[] = [];
  for (let keep = tails.length; keep >= 0; keep--) {
    const tail = tails.slice(0, keep).join(" ");
    candidates.push([build(biz, item), tail].filter(Boolean).join(" "));
  }
  for (const c of candidates) if (gsmLength(c) <= SMS_SINGLE_LIMIT) return c;
  // Still too long without tails: shorten the service/item, then the business name.
  for (const [b, i] of [[biz, clip(item, 30)], [clip(biz, 30), clip(item, 24)], [clip(biz, 20), clip(item, 16)]]) {
    const c = build(b, i);
    if (gsmLength(c) <= SMS_SINGLE_LIMIT) return c;
  }
  // Never cut the text itself (it may end in a link): a rare 2-part text is
  // better than a broken one.
  return build(clip(biz, 20), clip(item, 16));
}

/** "Tue 4 Nov at 10:00" in the business's timezone. */
export function formatSmsWhen(at: string | Date, timeZone = "Europe/London"): string {
  const d = typeof at === "string" ? new Date(at) : at;
  if (Number.isNaN(d.getTime())) return "";
  try {
    const day = new Intl.DateTimeFormat("en-GB", { timeZone, weekday: "short", day: "numeric", month: "short" }).format(d);
    const time = new Intl.DateTimeFormat("en-GB", { timeZone, hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
    return `${day.replace(",", "")} at ${time}`;
  } catch {
    return d.toISOString().slice(0, 16).replace("T", " ");
  }
}

/** "Tue 4 Nov at 10:00" straight from a booking's local date and time. */
export function formatSmsWallClock(date: string, time: string): string {
  const [y, m, d] = String(date).slice(0, 10).split("-").map(Number);
  const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1, 12));
  if (Number.isNaN(dt.getTime())) return "";
  const day = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", weekday: "short", day: "numeric", month: "short" }).format(dt);
  return `${day.replace(",", "")} at ${String(time).slice(0, 5)}`;
}

export interface BookingSmsInput {
  businessName: string;
  serviceName?: string | null;
  when: string;
  businessPhone?: string | null;
}

export function buildBookingConfirmationSms(input: BookingSmsInput): string {
  const biz = toGsmFriendly(input.businessName || "Your cleaner");
  const service = toGsmFriendly(input.serviceName || "booking");
  const when = toGsmFriendly(input.when);
  const tails = input.businessPhone ? [`Need to change it? Call ${toGsmFriendly(input.businessPhone)}.`] : [];
  return fit((b, s) => `${b}: your ${s} is confirmed for ${when}.`, biz, service, tails);
}

export function buildAppointmentReminderSms(input: BookingSmsInput): string {
  const biz = toGsmFriendly(input.businessName || "Your cleaner");
  const service = toGsmFriendly(input.serviceName || "appointment");
  const when = toGsmFriendly(input.when);
  const tails = input.businessPhone ? [`Need to change it? Call ${toGsmFriendly(input.businessPhone)}.`] : [];
  return fit((b, s) => `Reminder from ${b}: your ${s} is on ${when}.`, biz, service, tails);
}

export function buildQuoteSms(input: { businessName: string; quoteNumber?: string | null; url: string }): string {
  const biz = toGsmFriendly(input.businessName || "Your cleaner");
  const num = toGsmFriendly(input.quoteNumber || "");
  // The link must survive whole, so only the name/number get shortened.
  return fit((b, n) => `${b}: your quote${n ? ` ${n}` : ""} is ready. View and accept it here: ${input.url}`, biz, num, []);
}

export function buildTestSms(businessName: string): string {
  const biz = toGsmFriendly(businessName || "Your business");
  return fit((b) => `${b}: this is a test text. Your Twilio account is connected to The Polished Version.`, biz, "", []);
}

// ---------------------------------------------------------------------------
// Should we send?
// ---------------------------------------------------------------------------

export interface SmsDecisionInput {
  /** Twilio connected (row + token present). */
  connected: boolean;
  /** Plan includes texts (Team, Business, trial). */
  planAllows: boolean;
  /** The owner's toggle for this kind of text (tests are always on). */
  typeEnabled: boolean;
  /** Customer or number opted out. */
  optedOut: boolean;
  phone: string | null | undefined;
}

export type SmsDecision =
  | { send: true; to: string }
  | { send: false; reason: "not_connected" | "plan" | "disabled" | "opted_out" | "no_phone" | "not_mobile" };

export function decideSms(input: SmsDecisionInput): SmsDecision {
  if (!input.connected) return { send: false, reason: "not_connected" };
  if (!input.planAllows) return { send: false, reason: "plan" };
  if (!input.typeEnabled) return { send: false, reason: "disabled" };
  if (input.optedOut) return { send: false, reason: "opted_out" };
  const phone = normaliseUkMobile(input.phone);
  if (!phone.ok) return { send: false, reason: phone.reason === "empty" ? "no_phone" : "not_mobile" };
  return { send: true, to: phone.e164 };
}

export interface SmsToggles {
  send_booking_confirmation?: boolean | null;
  send_appointment_reminder?: boolean | null;
  send_quote_link?: boolean | null;
}

export function typeEnabled(toggles: SmsToggles, type: SmsMessageType): boolean {
  if (type === "test") return true;
  if (type === "booking_confirmation") return toggles.send_booking_confirmation !== false;
  if (type === "appointment_reminder") return toggles.send_appointment_reminder !== false;
  return toggles.send_quote_link !== false;
}

// ---------------------------------------------------------------------------
// Twilio errors and statuses
// ---------------------------------------------------------------------------

/** Twilio error: the recipient replied STOP. */
export const TWILIO_OPTED_OUT = 21610;

/** Errors that will never succeed on retry for this number/message. */
const PERMANENT_TWILIO_ERRORS = new Set([
  21211, // invalid To
  21212, // invalid From
  21408, // region not enabled
  21606, // From can't send
  21610, // opted out
  21612, // can't route
  21614, // not a mobile
  21659, // From not a Twilio number
  21660, // From not on this account
]);

export function isRetryableSmsFailure(httpStatus: number, errorCode: number | null | undefined): boolean {
  if (errorCode && PERMANENT_TWILIO_ERRORS.has(Number(errorCode))) return false;
  return httpStatus === 0 || httpStatus === 429 || httpStatus >= 500;
}

/** Plain-English reason for a Twilio failure, for the owner's log. */
export function describeTwilioError(code: number | string | null | undefined, fallback?: string | null): string {
  switch (Number(code)) {
    case 20003: return "Twilio didn't accept your Account SID and Auth Token.";
    case 21211: return "The customer's number isn't valid.";
    case 21408: return "Your Twilio account isn't allowed to text this country. Turn on UK in Twilio's Geo permissions.";
    case 21606:
    case 21659:
    case 21660: return "Your sending number can't send texts from this Twilio account.";
    case 21608: return "Twilio trial accounts can only text verified numbers. Upgrade your Twilio account or verify this number.";
    case 21610: return "This customer has replied STOP, so they won't get texts.";
    case 21612:
    case 21614: return "This number can't receive texts.";
    case 30003: return "The phone was switched off or out of signal.";
    case 30005: return "The number doesn't exist any more.";
    case 30006: return "That number is a landline or can't receive texts.";
    case 30007: return "The mobile network blocked the text.";
    case 30008: return "The text couldn't be delivered.";
    default: return fallback ? String(fallback).slice(0, 200) : "The text couldn't be sent.";
  }
}

const STATUS_RANK: Record<string, number> = {
  sending: 0, accepted: 1, scheduled: 1, queued: 1, sent: 2,
  delivered: 3, undelivered: 3, failed: 3, canceled: 3, read: 4,
};

/** Twilio callbacks can arrive out of order: never move a message backwards. */
export function shouldApplyStatus(current: string | null | undefined, next: string | null | undefined): boolean {
  if (!next) return false;
  const a = STATUS_RANK[String(current ?? "sending")] ?? 0;
  const b = STATUS_RANK[String(next)] ?? 0;
  return b >= a;
}

// ---------------------------------------------------------------------------
// Webhook signature (X-Twilio-Signature)
// ---------------------------------------------------------------------------

function toBase64(bytes: ArrayBuffer): string {
  let s = "";
  const view = new Uint8Array(bytes);
  for (let i = 0; i < view.length; i++) s += String.fromCharCode(view[i]);
  return btoa(s);
}

/** Twilio's signature for a form-encoded POST: url + sorted key+value pairs. */
export async function twilioSignature(
  authToken: string,
  url: string,
  params: Record<string, string | string[]> | URLSearchParams,
): Promise<string> {
  const map = new Map<string, string[]>();
  if (params instanceof URLSearchParams) {
    for (const [k, v] of params) map.set(k, [...(map.get(k) ?? []), v]);
  } else {
    for (const [k, v] of Object.entries(params)) map.set(k, Array.isArray(v) ? v : [v]);
  }
  let data = url;
  for (const key of [...map.keys()].sort()) {
    const values = [...new Set(map.get(key))].sort();
    for (const v of values) data += key + v;
  }
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey("raw", enc.encode(authToken), { name: "HMAC", hash: "SHA-1" }, false, ["sign"]);
  return toBase64(await crypto.subtle.sign("HMAC", key, enc.encode(data)));
}

function safeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/** The same URL with and without the default port, as Twilio's own validator tries. */
function urlVariants(url: string): string[] {
  const out = [url];
  const m = url.match(/^(https?):\/\/([^/?#:]+)(:\d+)?(.*)$/i);
  if (m) {
    const [, scheme, host, port, rest] = m;
    if (port) out.push(`${scheme}://${host}${rest}`);
    else out.push(`${scheme}://${host}:${scheme.toLowerCase() === "https" ? 443 : 80}${rest}`);
  }
  return out;
}

export async function verifyTwilioSignature(
  authToken: string | null | undefined,
  signature: string | null | undefined,
  url: string,
  params: Record<string, string | string[]> | URLSearchParams,
): Promise<boolean> {
  if (!authToken || !signature) return false;
  for (const candidate of urlVariants(url)) {
    if (safeEqual(await twilioSignature(authToken, candidate, params), signature)) return true;
  }
  return false;
}
